import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '../../design-system/cn';
import {
  zIndex,
  motion,
  spacing,
  consoleModal,
  consoleTypography,
  consoleMotion,
  touchTarget,
} from '../../design-system/tokens';
import { closeTopModal, isTopModal, popModal, pushModal } from '../../utils/modalStack';
import Button from './Button';

const ACCENT_BORDERS = {
  brand: consoleModal.accent.brand,
  brandStrong: consoleModal.accent.brandStrong,
  live: consoleModal.accent.live,
  wallet: consoleModal.accent.wallet,
  custom: consoleModal.accent.wallet,
  friends: consoleModal.accent.friends,
  admin: 'border-white/[0.12]',
  danger: consoleModal.accent.danger,
  dangerStrong: consoleModal.accent.dangerStrong,
  warning: consoleModal.accent.warning,
  neutral: consoleModal.accent.neutral,
};

export function ModalScrim({ onClose, className, visible = true }) {
  if (!visible) return null;
  return (
    <div
      className={cn('absolute inset-0', consoleModal.scrim, className)}
      onClick={onClose}
      aria-hidden
    />
  );
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  header,
  accent = 'brand',
  size = 'md',
  z = zIndex.modal,
  bodyClassName,
  panelClassName,
  scrimClassName,
  children,
  footer,
  className,
}) {
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const stackIdRef = useRef(null);
  const didInitialFocusRef = useRef(false);
  const [stackId, setStackId] = useState(null);
  const reactId = useId();

  onCloseRef.current = onClose;
  stackIdRef.current = stackId;

  useLayoutEffect(() => {
    if (!open) {
      setStackId(null);
      return undefined;
    }

    const id = pushModal(() => onCloseRef.current?.(), z);
    setStackId(id);
    return () => {
      popModal(id);
      setStackId((current) => (current === id ? null : current));
    };
  }, [open, z]);

  useEffect(() => {
    if (!open) {
      didInitialFocusRef.current = false;
      return undefined;
    }

    if (didInitialFocusRef.current) {
      return undefined;
    }
    didInitialFocusRef.current = true;

    const focusPanel = () => {
      const panel = panelRef.current;
      if (!panel) return;
      const active = document.activeElement;
      if (
        active
        && panel.contains(active)
        && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.tagName === 'SELECT')
      ) {
        return;
      }
      const focusable = panel.querySelector(FOCUSABLE);
      if (focusable && typeof focusable.focus === 'function') {
        focusable.focus({ preventScroll: true });
      } else {
        panel.setAttribute('tabindex', '-1');
        panel.focus({ preventScroll: true });
      }
    };
    const focusTimer = window.setTimeout(focusPanel, 0);

    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (!isTopModal(stackIdRef.current)) return;
      e.preventDefault();
      e.stopPropagation();
      onCloseRef.current?.();
    };
    window.addEventListener('keydown', onKey, true);

    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  if (!open) return null;

  const isTop = isTopModal(stackId);
  const maxW =
    size === 'sm' ? 'max-w-sm' : size === 'lg' ? 'max-w-2xl' : size === 'xl' ? 'max-w-4xl' : 'max-w-md';

  const handleScrimClose = () => {
    if (!isTopModal(stackId)) return;
    onCloseRef.current?.();
  };

  return (
    <div
      className="fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4 max-md:overflow-x-hidden max-md:overflow-y-auto sm:overflow-hidden"
      style={{ zIndex: z }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      data-modal-layer={reactId}
    >
      <ModalScrim
        onClose={handleScrimClose}
        className={scrimClassName}
        visible={isTop}
      />
      <div
        ref={panelRef}
        className={cn(
          'relative z-10 w-full max-w-[100vw] overflow-hidden flex flex-col min-w-0',
          consoleModal.panel,
          consoleModal.panelMobile,
          'max-h-[min(92dvh,100%)] sm:max-h-[90vh]',
          motion.modalEnter,
          'pb-[max(1rem,env(safe-area-inset-bottom))]',
          ACCENT_BORDERS[accent] || ACCENT_BORDERS.neutral,
          maxW,
          !isTop && 'pointer-events-none',
          panelClassName,
          className,
        )}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {header ?? ((title || onClose) ? (
          <ModalHeader title={title} subtitle={subtitle} onClose={onClose} />
        ) : null)}
        <div className={cn('flex-1 overflow-y-auto overflow-x-hidden overscroll-contain min-w-0', bodyClassName ?? spacing.modal.body)}>
          {children}
        </div>
        {footer && (
          <div className={cn('shrink-0 border-t', consoleModal.footerBorder, spacing.modal.footer)}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export function ModalHeader({ title, subtitle, onClose, className }) {
  return (
    <div
      className={cn(
        'shrink-0 flex items-start justify-between gap-4 p-5 md:p-6 border-b',
        consoleModal.headerBorder,
        className,
      )}
    >
      <div className="text-left flex-1 min-w-0">
        {title && (
          typeof title === 'string' ? (
            <h2 id="modal-title" className={cn(consoleTypography.section, 'text-lg md:text-xl')}>
              {title}
            </h2>
          ) : (
            title
          )
        )}
        {subtitle && (
          <p className={cn(consoleTypography.meta, 'mt-1')}>
            {subtitle}
          </p>
        )}
      </div>
      {onClose && (
        <Button
          variant="close"
          type="button"
          onClick={onClose}
          aria-label="Close"
          className={cn(touchTarget.comfortable, consoleMotion.focus)}
        >
          ×
        </Button>
      )}
    </div>
  );
}

export { closeTopModal };
